import { createInterfaceId } from '@gitlab/needle';
import { Logger } from '@gitlab-org/logging';
import {
  type ChatLog,
  type DuoWorkflowEvent,
  type WorkflowTool,
  type DelegationMarker,
  DELEGATE_TASK_TOOL_NAME,
  DELEGATION_SUB_TYPE,
  isPlanApprovalRequest,
  parseCheckpoint,
  parseDelegationMarker,
  parseStreamingDelegation,
} from '@gitlab-lsp/workflow-api';
import {
  AgentEventType,
  UserEventType,
  type AgentEvent,
  type ApprovalScope,
  type EntryAttribution,
  type SessionEvent,
} from '../backend';
import { ToolInputFormatterService } from '../tool_input_formatter';

/**
 * Key under which the backend reports the primary agent's context usage in the
 * per-agent `agent_context_usage` map. Coupled to the Duo Workflow Service
 * naming convention; this is the single place to update if the backend renames it.
 */
const PREFERRED_AGENT_KEY = 'agent';

/** Shown when the backend reports a failed delegation without saying why. */
const DELEGATION_FAILED_MESSAGE = 'The sub-agent did not return an answer.';

export interface WorkflowEventMapper {
  mapWorkflowEvent(
    duoEvent: DuoWorkflowEvent,
    availableScopes: ApprovalScope[],
  ): Promise<AgentEvent[]>;
  mapChatLogToSessionEvents(chatLog: ChatLog[]): Promise<SessionEvent[]>;
  advanceToResumePoint(chatLog: ChatLog[]): void;
}

export const WorkflowEventMapper = createInterfaceId<WorkflowEventMapper>('WorkflowEventMapper');

export class WorkflowEventMapperService implements WorkflowEventMapper {
  #toolInputFormatter: ToolInputFormatterService;

  #logger: Logger;

  #lastMessageContent = '';

  #lastMessageId = '';

  /**
   * The ui_chat_log index of the agent message #lastMessageId/#lastMessageContent
   * refer to. "Same message still streaming" is decided by this index, not by
   * re-deriving an id every call, since message_id can arrive a checkpoint or
   * two after streaming starts and would otherwise change the id mid-stream.
   */
  #lastMessageIndex = -1;

  /**
   * The number of messages that were present in the last processed checkpoint.
   * Used to detect when a checkpoint adds multiple new messages at once
   * (e.g. parallel tool calls), so we can process each new message individually.
   */
  #lastProcessedMessageCount = 0;

  /**
   * Streamed tool calls with an open `ToolStart` that haven't settled yet,
   * keyed by resolved tool id. PENDING entries are transient and usually
   * gone from `workflowMessages` by the time the call settles, so we track
   * them here instead.
   */
  #openToolStarts = new Map<string, { name: string; args: Record<string, unknown> }>();

  /**
   * Tool ids we've already emitted a `ToolComplete` for. A still-PENDING tool
   * entry elsewhere in the same checkpoint keeps `#lastProcessedMessageCount`
   * from advancing, so an already-settled entry can be re-processed across
   * several checkpoints (its `#openToolStarts` entry is long gone by then) —
   * without this guard that would re-emit a spurious `ToolAwaitingApproval` +
   * duplicate `ToolComplete` for it.
   */
  #settledToolIds = new Set<string>();

  constructor(toolInputFormatter: ToolInputFormatterService, logger: Logger) {
    this.#toolInputFormatter = toolInputFormatter;
    this.#logger = logger;
  }

  /**
   * Seeds the live-streaming cursor when a session is resumed so the first
   * post-resume checkpoint, which carries the full rehydrated history, does not
   * re-emit the whole conversation.
   *
   * A trailing agent message keeps the cursor just before it, so a resumed turn
   * re-emits it as a full message (and can keep streaming it) — the session layer
   * dedupes by element id and replaces the rehydrated element in place. We do NOT
   * seed #lastMessageId / #lastMessageContent: leaving them unset makes the first
   * post-resume event for any id carry full content, which keeps the full-vs-delta
   * decision unambiguous for the session layer. Any other trailing type means the
   * history is already settled, so the whole chat log is treated as processed.
   */
  advanceToResumePoint(chatLog: ChatLog[]): void {
    if (chatLog.length === 0) return;

    const lastIndex = chatLog.length - 1;
    this.#lastProcessedMessageCount =
      chatLog[lastIndex].message_type === 'agent' ? lastIndex : chatLog.length;
  }

  /**
   * A stable element id for a request/tool entry: prefer the backend's
   * message_id, fall back to the array index. Unlike streaming agent text these
   * entries are never re-emitted token-by-token, so their id does not flip
   * mid-turn and no index-pinning is needed. message_id is still required so a
   * rolled-back checkpoint that recycles an array index does not collide with
   * the orphaned tool card the cancelled turn left behind.
   */
  #stableToolId(entry: ChatLog, index: number): string {
    return entry.message_id ?? `${index}`;
  }

  #parseTimestamp(timestamp: string): number {
    const parsed = Date.parse(timestamp);
    return Number.isNaN(parsed) ? Date.now() : parsed;
  }

  /** Undefined when the flow reports neither field, as the legacy chat flow does. */
  #attribution(entry: ChatLog): EntryAttribution | undefined {
    if (entry.component_name === undefined && entry.subsession_id === undefined) return undefined;
    return {
      ...(entry.component_name !== undefined ? { componentName: entry.component_name } : {}),
      ...(entry.subsession_id !== undefined ? { subsessionId: entry.subsession_id } : {}),
    };
  }

  /** The tree's view of a delegation boundary: which sub-session opened or closed. */
  #delegationMarkerEvent(entry: ChatLog, marker: DelegationMarker): AgentEvent {
    const attribution = this.#attribution(entry);
    return {
      type: AgentEventType.Delegation,
      phase: marker.phase,
      markerId: this.#delegationMarkerId(entry, marker),
      ...(marker.subagentName !== undefined ? { subagentName: marker.subagentName } : {}),
      ...(marker.subsessionId !== undefined ? { subsessionId: marker.subsessionId } : {}),
      ...(marker.prompt !== undefined ? { prompt: marker.prompt } : {}),
      ...(attribution !== undefined ? { attribution } : {}),
      timestamp: this.#parseTimestamp(entry.timestamp),
    };
  }

  /**
   * `message_id` is shared by a delegation and its return, so contents decide identity.
   * JSON-encoded rather than delimiter-joined: the name comes from LLM-produced tool
   * args, and two distinct markers whose ids alias would make the session's dedup drop
   * the real second one.
   */
  #delegationMarkerId(entry: ChatLog, marker: DelegationMarker): string {
    const identity = JSON.stringify([
      marker.phase,
      marker.subagentName ?? null,
      marker.subsessionId ?? null,
    ]);
    return entry.message_id ? `${entry.message_id}:${identity}` : identity;
  }

  async #delegationEvents(
    entry: ChatLog,
    index: number,
    messages: ChatLog[],
  ): Promise<AgentEvent[] | undefined> {
    const marker = parseDelegationMarker(entry);
    if (!marker || entry.message_type !== 'tool') return undefined;

    if (marker.phase === 'started' && marker.subagentName === undefined) return [];

    return [
      this.#delegationMarkerEvent(entry, marker),
      ...(await this.#delegationCardEvents(entry, index, messages, marker)),
    ];
  }

  async #delegationCardEvents(
    entry: ChatLog & { message_type: 'tool' },
    index: number,
    messages: ChatLog[],
    marker: DelegationMarker,
  ): Promise<AgentEvent[]> {
    const timestamp = this.#parseTimestamp(entry.timestamp);
    const attribution = this.#attribution(entry);

    if (marker.phase === 'started') {
      return [await this.#delegationToolStart(entry, this.#stableToolId(entry, index))];
    }

    const opening = this.#openingDelegationEntry(entry, index, messages);
    const toolId = opening
      ? this.#stableToolId(opening.entry, opening.index)
      : this.#stableToolId(entry, index);

    if (this.#settledToolIds.has(toolId)) return [];

    const output = this.#extractToolOutput(entry);

    return [
      ...(opening ? [] : [await this.#delegationToolStart(entry, toolId)]),
      {
        type: AgentEventType.ToolComplete,
        toolId,
        ...(marker.phase === 'failed'
          ? { result: '', error: output || DELEGATION_FAILED_MESSAGE }
          : { result: output }),
        timestamp,
        attribution,
      },
    ];
  }

  async #delegationToolStart(
    entry: ChatLog & { message_type: 'tool' },
    toolId: string,
  ): Promise<AgentEvent> {
    return {
      type: AgentEventType.ToolStart,
      toolId,
      name: DELEGATE_TASK_TOOL_NAME,
      input: await this.#toolInputFormatter.formatToolInput(
        DELEGATE_TASK_TOOL_NAME,
        entry.tool_info?.args ?? {},
        // Pending for the sub-agent's whole run, so strict parsing would warn every checkpoint.
        { partial: true },
      ),
      timestamp: this.#parseTimestamp(entry.timestamp),
      attribution: this.#attribution(entry),
    };
  }

  #openingDelegationEntry(
    entry: ChatLog,
    index: number,
    messages: ChatLog[],
  ): { entry: ChatLog; index: number } | undefined {
    if (entry.message_id !== undefined) {
      for (let i = index - 1; i >= 0; i--) {
        const candidate = messages[i];
        if (
          candidate.message_sub_type === DELEGATION_SUB_TYPE &&
          candidate.message_id === entry.message_id
        ) {
          return { entry: candidate, index: i };
        }
      }
    }
    return this.#openDelegationAt(index, messages);
  }

  #openDelegationAt(
    index: number,
    messages: ChatLog[],
  ): { entry: ChatLog; index: number } | undefined {
    for (let i = index - 1; i >= 0; i--) {
      const candidate = messages[i];
      const phase = parseDelegationMarker(candidate)?.phase;
      if (phase !== undefined) {
        return phase === 'started' ? { entry: candidate, index: i } : undefined;
      }
    }
    return undefined;
  }

  /**
   * Checks if two tool args are equivalent for matching purposes
   */
  #argsMatch(args1: Record<string, unknown>, args2: Record<string, unknown>): boolean {
    // For command-based tools, compare program field
    if ('program' in args1 && 'program' in args2) {
      return args1.program === args2.program;
    }

    // For other tools, do deep equality check via JSON comparison
    return JSON.stringify(args1) === JSON.stringify(args2);
  }

  /** Finds and removes a still-open streamed tool call matching `toolId` exactly. */
  #takeOpenToolStart(
    toolId: string,
  ): { toolId: string; name: string; args: Record<string, unknown> } | undefined {
    const exact = this.#openToolStarts.get(toolId);
    if (!exact) return undefined;
    this.#openToolStarts.delete(toolId);
    return { toolId, ...exact };
  }

  /**
   * A tool call's stable id. DWS prefixes the approval request's id with
   * `request-`; stripping it lets the request, PENDING, and settled entries
   * resolve to the same id. Falls back to a positional id when `message_id`
   * is absent.
   */
  #resolveToolId(messageId: string | undefined, fallback: string): string {
    if (!messageId) return fallback;
    const REQUEST_PREFIX = 'request-';
    return messageId.startsWith(REQUEST_PREFIX)
      ? messageId.slice(REQUEST_PREFIX.length)
      : messageId;
  }

  /**
   * The backend pairs a no-op or failed compaction tool card with a separate
   * agent message carrying the explanation (other clients ignore tool-card
   * content, so it is sent as its own entry). The CLI renders the compaction
   * card itself, so this notice is redundant. Per the backend contract the only
   * agent message that can directly follow a compaction tool card is this notice.
   *
   * Keyed on `tool_info.name === 'compaction'` — the same signal the formatter
   * uses to route the compaction card (see CompactionFormatter) — so the card
   * rendering and this suppression can never disagree about what is a
   * compaction entry.
   */
  #isCompactionNotice(messages: ChatLog[], index: number): boolean {
    if (index <= 0) return false;
    const previous = messages[index - 1];
    return previous.message_type === 'tool' && previous.tool_info?.name === 'compaction';
  }

  /**
   * Maps a single incoming workflow event to one or more agent events
   * for the UI layer, handling streaming text deltas and tool lifecycle.
   *
   * @param duoEvent The workflow event from the backend
   * @param availableScopes Approval scopes available for this workflow (from capability check)
   */
  async mapWorkflowEvent(
    duoEvent: DuoWorkflowEvent,
    availableScopes: ApprovalScope[],
  ): Promise<AgentEvent[]> {
    const events: AgentEvent[] = [];

    const checkpointResult = parseCheckpoint(duoEvent);
    if (checkpointResult.isErr()) {
      this.#logger.error('Failed to parse workflow checkpoint', checkpointResult.error);
      return events;
    }

    // Record context usage only after the checkpoint parses. On a parse error we
    // discard the chat log, so bumping the usage percentage here would tick the
    // indicator upward for a turn the user never sees.
    const tokenUsageEvent = this.#mapTokenUsage(duoEvent.agentContextUsage);
    if (tokenUsageEvent) {
      events.push(tokenUsageEvent);
    }

    const { chatLog: workflowMessages, nodeEvents } = checkpointResult.value;
    if (nodeEvents.length > 0) {
      events.push({
        type: AgentEventType.NodeLifecycle,
        events: nodeEvents,
        timestamp: Date.now(),
      });
    }

    if (workflowMessages.length === 0) {
      return events;
    }

    // Cancelling a turn mid-stream makes DWS roll back to the last stable
    // checkpoint boundary, so ui_chat_log can shrink. Resync fully (unlike
    // advanceToResumePoint, which assumes the trailing message may still be
    // streaming) since a rolled-back checkpoint is always fully settled.
    if (workflowMessages.length < this.#lastProcessedMessageCount) {
      this.#lastProcessedMessageCount = workflowMessages.length;
      this.#lastMessageId = '';
      this.#lastMessageContent = '';
      this.#lastMessageIndex = -1;
    }

    const latestMessage = workflowMessages[workflowMessages.length - 1];

    // Always process from #lastProcessedMessageCount so that checkpoints which
    // batch multiple new entries (e.g. ToolsExecutor emitting an agent text
    // message followed by one or more tool results in a single state update)
    // are handled correctly regardless of which message type is last.
    //
    // We do NOT advance #lastProcessedMessageCount when the last message is an
    // agent message, because the backend streams agent content in-place: the
    // same index keeps receiving more content until the turn is complete.
    // Keeping the count stable lets the delta mechanism re-process it on the
    // next checkpoint and emit only the incremental text chunk.
    //
    // Same applies to a still-PENDING tool entry: the backend rewrites it in
    // place with progressively more complete args until it settles.
    const latestIsStreamingToolCall =
      latestMessage.message_type === 'tool' && latestMessage.status === 'pending';
    const newMessagesStartIndex = this.#lastProcessedMessageCount;
    if (latestMessage.message_type !== 'agent' && !latestIsStreamingToolCall) {
      this.#lastProcessedMessageCount = workflowMessages.length;
    }

    const messagePromises = workflowMessages
      .slice(newMessagesStartIndex)
      .map((message, offset) =>
        this.#processMessage(
          message,
          newMessagesStartIndex + offset,
          workflowMessages,
          availableScopes,
          newMessagesStartIndex,
        ),
      );
    const newEventBatches = await Promise.all(messagePromises);
    for (const batch of newEventBatches) {
      events.push(...this.#withoutReopenedCards(batch));
    }

    return events;
  }

  /**
   * Selects one entry from the per-agent context-usage map and maps it to a
   * TokenUsage event. Selection rule: prefer the entry keyed `agent`; otherwise
   * use the sole entry when there is exactly one; otherwise emit nothing.
   *
   * Returns undefined (no event) when the map is missing/empty, no entry can be
   * selected, or the values are unusable (non-finite, totalTokens <= 0, or
   * maxTokens <= 0). The threshold/percentage formatting lives in the TUI; this
   * mapper only forwards the raw counts.
   */
  #mapTokenUsage(
    agentContextUsage: DuoWorkflowEvent['agentContextUsage'],
  ): (AgentEvent & { type: AgentEventType.TokenUsage }) | undefined {
    if (!agentContextUsage) return undefined;

    const entries = Object.entries(agentContextUsage);
    const usage =
      agentContextUsage[PREFERRED_AGENT_KEY] ?? (entries.length === 1 ? entries[0][1] : undefined);
    if (!usage) return undefined;

    const { totalTokens, maxTokens } = usage;
    if (!Number.isFinite(totalTokens) || totalTokens <= 0) return undefined;
    if (!Number.isFinite(maxTokens) || maxTokens <= 0) return undefined;

    return {
      type: AgentEventType.TokenUsage,
      totalTokens,
      maxTokens,
      timestamp: Date.now(),
    };
  }

  #withoutReopenedCards(batch: AgentEvent[]): AgentEvent[] {
    return batch.filter((event) => {
      if (event.type === AgentEventType.ToolComplete) {
        this.#settledToolIds.add(event.toolId);
        return true;
      }
      return event.type !== AgentEventType.ToolStart || !this.#settledToolIds.has(event.toolId);
    });
  }

  async #processMessage(
    message: ChatLog,
    messageIndex: number,
    workflowMessages: ChatLog[],
    availableScopes: ApprovalScope[],
    newMessagesStartIndex: number = 0,
  ): Promise<AgentEvent[]> {
    const events: AgentEvent[] = [];

    const delegationEvents = await this.#delegationEvents(message, messageIndex, workflowMessages);
    if (delegationEvents) return delegationEvents;

    const attribution = this.#attribution(message);

    // Only process assistant and tool messages - user messages are handled by the controller
    switch (message.message_type) {
      case 'user':
        // Skip user messages
        break;

      case 'agent': {
        const currentContent = message.content;

        // Skip agent messages with no content — nothing to render
        if (!currentContent) break;

        // Skip the redundant compaction notice paired with a compaction card
        if (this.#isCompactionNotice(workflowMessages, messageIndex)) break;

        // A new index prefers the backend's stable message_id over the array
        // index, since a rolled-back checkpoint can hand out the same index to
        // an unrelated message and an index-based id would then collide with
        // the stale, orphaned element it left behind.
        const isContinuationOfLastMessage = messageIndex === this.#lastMessageIndex;
        const currentId = isContinuationOfLastMessage
          ? this.#lastMessageId
          : (message.message_id ?? `${messageIndex}`);
        const timestamp = this.#parseTimestamp(message.timestamp);

        this.#lastMessageIndex = messageIndex;

        // Check if this is the same message being updated (same ID)
        if (isContinuationOfLastMessage) {
          // Compute delta from last known content
          if (!currentContent.startsWith(this.#lastMessageContent)) {
            this.#logger.error(
              `Workflow Service replaced message content unexpectedly. Message ID: ${currentId} Previous message: "${this.#lastMessageContent}", Current message: "${currentContent}"`,
            );
            events.push({
              type: AgentEventType.TextChunk,
              messageId: currentId,
              content: currentContent,
              timestamp,
              attribution,
            });
            this.#lastMessageContent = currentContent;
          }
          const delta = currentContent.slice(this.#lastMessageContent.length);
          if (delta.length > 0) {
            events.push({
              type: AgentEventType.TextChunk,
              messageId: currentId,
              content: delta,
              timestamp,
              attribution,
            });
            this.#lastMessageContent = currentContent;
          }
        } else {
          // New message (different ID)
          events.push({
            type: AgentEventType.TextChunk,
            messageId: currentId,
            content: currentContent,
            timestamp,
            attribution,
          });
          this.#lastMessageContent = currentContent;
          this.#lastMessageId = currentId;
        }
        break;
      }

      case 'request': {
        if (isPlanApprovalRequest(message)) break;
        // If a matching tool result also appears in the same new batch, the tool
        // was auto-approved and already ran. Skip the approval UI in that case —
        // the tool case below will emit ToolAwaitingApproval + ToolComplete instead.
        const hasMatchingToolInBatch = workflowMessages
          .slice(newMessagesStartIndex)
          .some(
            (m) =>
              m.message_type === 'tool' &&
              m.tool_info !== null &&
              message.tool_info !== null &&
              m.tool_info.name === message.tool_info.name &&
              this.#argsMatch(m.tool_info.args, message.tool_info.args),
          );

        if (!hasMatchingToolInBatch) {
          const toolId = this.#resolveToolId(message.message_id, `${messageIndex}`);
          // A real approval request supersedes any open streamed ToolStart for the same id.
          this.#takeOpenToolStart(toolId);
          events.push(
            await this.#createToolAwaitingApprovalEvent(
              toolId,
              message.tool_info.name,
              message.tool_info.args,
              message.content,
              this.#parseTimestamp(message.timestamp),
              availableScopes,
              { suggestedPatterns: message.tool_info.suggested_patterns, attribution },
            ),
          );
        }
        break;
      }

      case 'tool': {
        const timestamp = this.#parseTimestamp(message.timestamp);

        // Still-streaming tool call: surface it as a ToolStart so the UI
        // shows progress; later checkpoints re-emit ToolStart to update it.
        if (message.status === 'pending') {
          const toolId = this.#resolveToolId(message.message_id, `${messageIndex}`);
          const toolName = message.tool_info?.name ?? 'unknown tool';
          const toolArgs = message.tool_info?.args ?? {};
          this.#openToolStarts.set(toolId, { name: toolName, args: toolArgs });
          const streamedDelegation = parseStreamingDelegation(message);
          if (streamedDelegation) {
            events.push(this.#delegationMarkerEvent(message, streamedDelegation));
          }
          events.push({
            type: AgentEventType.ToolStart,
            toolId,
            name: toolName,
            input: await this.#toolInputFormatter.formatToolInput(
              toolName,
              message.tool_info?.args ?? {},
              { partial: true },
            ),
            timestamp,
            attribution,
          });
          break;
        }

        // Match approval requests with tool use
        // this craziness is here because tool approval request and tool use are completely different messages from the workflow service perspective
        // We are trying to combine them here so we can show unified tool element for the request and then the call.
        let approvalRequestIndex: number | undefined;
        for (let i = messageIndex - 1; i >= 0; i--) {
          const prevMsg = workflowMessages[i];
          if (
            prevMsg.message_type === 'request' &&
            prevMsg.tool_info !== null &&
            message.tool_info !== null &&
            prevMsg.tool_info.name === message.tool_info.name &&
            this.#argsMatch(prevMsg.tool_info.args, message.tool_info.args)
          ) {
            approvalRequestIndex = i;
            break;
          }
        }

        // Match this settled entry back to a still-open streamed ToolStart by exact id.
        const candidateToolId = this.#resolveToolId(message.message_id, `${messageIndex}`);
        const matchedOpenStart =
          approvalRequestIndex === undefined && message.tool_info
            ? this.#takeOpenToolStart(candidateToolId)
            : undefined;

        const toolId =
          approvalRequestIndex !== undefined
            ? this.#resolveToolId(
                workflowMessages[approvalRequestIndex].message_id,
                `${approvalRequestIndex}`,
              )
            : (matchedOpenStart?.toolId ?? candidateToolId);

        // Already emitted ToolComplete for this id in an earlier checkpoint —
        // re-processing it now (see #settledToolIds) would otherwise emit a
        // spurious ToolAwaitingApproval + duplicate ToolComplete.
        if (this.#settledToolIds.has(toolId)) break;

        // Skip the synthetic "fake" ToolAwaitingApproval when the UI already has a
        // card for this id (a real approval request or a matched ToolStart).
        const alreadyStarted = approvalRequestIndex !== undefined || Boolean(matchedOpenStart);
        if (!alreadyStarted) {
          const toolName = message.tool_info?.name || 'unknown tool';
          events.push(
            await this.#createToolAwaitingApprovalEvent(
              toolId,
              toolName,
              message.tool_info?.args || {},
              message.content,
              timestamp,
              availableScopes,
              { attribution },
            ),
          );
        }

        const output = this.#extractToolOutput(message);

        // When a tool call fails, the node_executor sends a response to DWS like `{ result: '', error: 'Action error: <tool failed reason>' }`
        // However, the next ui_chat_log entry we receive contains a tool entry with `status: 'success'`, so in order to display the tool call as
        // failed we have to check the content for this known string.
        events.push(
          await this.#createToolCompleteEvent(
            toolId,
            output,
            timestamp,
            message.tool_info?.name,
            message.tool_info?.args,
            attribution,
          ),
        );
        this.#settledToolIds.add(toolId);

        break;
      }

      default:
        break;
    }

    return events;
  }

  /**
   * Converts a complete chat log into session events suitable for
   * replaying or restoring a previous workflow session in the UI.
   */
  async mapChatLogToSessionEvents(chatLog: ChatLog[]): Promise<SessionEvent[]> {
    const entryResults = await Promise.all(
      chatLog.map((entry, i) => this.#mapChatLogEntry(entry, i, chatLog)),
    );
    return entryResults.flat();
  }

  async #mapChatLogEntry(
    entry: ChatLog,
    index: number,
    chatLog: ChatLog[],
  ): Promise<SessionEvent[]> {
    const timestamp = this.#parseTimestamp(entry.timestamp);
    const messageId = `${index}`;
    const attribution = this.#attribution(entry);

    const delegationEvents = await this.#delegationEvents(entry, index, chatLog);
    if (delegationEvents) return delegationEvents;

    switch (entry.message_type) {
      case 'user':
        return [{ type: UserEventType.UserMessage, messageId, content: entry.content, timestamp }];

      case 'agent':
        if (!entry.content) return [];
        // Skip the redundant compaction notice paired with a compaction card
        if (this.#isCompactionNotice(chatLog, index)) return [];
        // Must agree with #processMessage's id choice: on resume, the live
        // mapper re-emits the trailing agent message and the session dedupes
        // by id against this rehydration, so a mismatch renders a duplicate.
        return [
          {
            type: AgentEventType.TextChunk,
            messageId: entry.message_id ?? messageId,
            content: entry.content,
            timestamp,
            attribution,
          },
        ];

      case 'request':
        return this.#mapRequestEntry(
          entry,
          index,
          chatLog,
          this.#stableToolId(entry, index),
          timestamp,
          attribution,
        );

      case 'tool':
        return this.#mapToolEntry(
          entry,
          index,
          chatLog,
          this.#stableToolId(entry, index),
          timestamp,
          attribution,
        );

      default:
        return [];
    }
  }

  async #mapRequestEntry(
    entry: ChatLog & { message_type: 'request' },
    index: number,
    chatLog: ChatLog[],
    messageId: string,
    timestamp: number,
    attribution?: EntryAttribution,
  ): Promise<AgentEvent[]> {
    const events: AgentEvent[] = [];
    if (isPlanApprovalRequest(entry)) return events;
    const matchingToolIndex = this.#findMatchingToolExecution(chatLog, index);
    const isTrailingRequest = index === chatLog.length - 1;

    // For historical chat log rehydration, default to ['once'] (no session approval for past events)
    events.push(
      await this.#createToolAwaitingApprovalEvent(
        messageId,
        entry.tool_info.name,
        entry.tool_info.args,
        entry.content,
        timestamp,
        ['once'],
        { attribution },
      ),
    );

    if (matchingToolIndex !== -1) {
      const toolEntry = chatLog[matchingToolIndex];
      if (toolEntry.message_type !== 'tool') return events;
      const toolTimestamp = this.#parseTimestamp(toolEntry.timestamp);
      const output = this.#extractToolOutput(toolEntry);

      events.push(
        await this.#createToolCompleteEvent(
          messageId,
          output,
          toolTimestamp,
          undefined,
          undefined,
          attribution,
        ),
      );
    } else if (!isTrailingRequest) {
      // A trailing tool approval without a matching resolution (approved, rejected) means
      // the session was closed without the user responding.
      // For non-trailing approvals, we synthesise a ToolComplete: the on-replay tool state
      // starts as `approval_request` when ToolAwaitingApproval is processed, and without a
      // following ToolComplete it would stay in that state — incorrectly re-presenting the
      // approval prompt for a tool the agent has already moved past.
      events.push({
        type: AgentEventType.ToolComplete,
        toolId: messageId,
        result: '',
        timestamp,
        attribution,
      });
    }

    return events;
  }

  async #mapToolEntry(
    entry: ChatLog & { message_type: 'tool' },
    index: number,
    chatLog: ChatLog[],
    messageId: string,
    timestamp: number,
    attribution?: EntryAttribution,
  ): Promise<AgentEvent[]> {
    if (this.#hasMatchingRequest(chatLog, index)) return [];

    const toolName = entry.tool_info?.name ?? 'unknown tool';
    const output = this.#extractToolOutput(entry);

    // For historical chat log rehydration, default to ['once'] (no session approval for past events)
    return [
      await this.#createToolAwaitingApprovalEvent(
        messageId,
        toolName,
        entry.tool_info?.args ?? {},
        entry.content,
        timestamp,
        ['once'],
        { attribution },
      ),
      await this.#createToolCompleteEvent(
        messageId,
        output,
        timestamp,
        undefined,
        undefined,
        attribution,
      ),
    ];
  }

  async #createToolAwaitingApprovalEvent(
    toolId: string,
    toolName: string,
    args: Record<string, unknown>,
    content: string,
    timestamp: number,
    availableScopes: ApprovalScope[],
    opts: { suggestedPatterns?: string[]; attribution?: EntryAttribution } = {},
  ): Promise<AgentEvent & { type: AgentEventType.ToolAwaitingApproval }> {
    return {
      type: AgentEventType.ToolAwaitingApproval,
      toolId,
      toolName,
      input: await this.#toolInputFormatter.formatToolInput(toolName, args),
      content,
      timestamp,
      availableScopes,
      suggestedPatterns: opts.suggestedPatterns,
      attribution: opts.attribution,
    };
  }

  /**
   * @param toolName When provided (streaming settle path), re-formats the
   * card's name/input from the final args to replace a partial PENDING
   * render. Omitted for session-replay, which already has complete args.
   */
  async #createToolCompleteEvent(
    toolId: string,
    output: string,
    timestamp: number,
    toolName?: string,
    toolArgs?: Record<string, unknown>,
    attribution?: EntryAttribution,
  ): Promise<AgentEvent & { type: AgentEventType.ToolComplete }> {
    const refreshed =
      toolName !== undefined
        ? {
            name: toolName,
            input: await this.#toolInputFormatter.formatToolInput(toolName, toolArgs ?? {}),
          }
        : {};
    return output.startsWith('Action error:')
      ? {
          type: AgentEventType.ToolComplete,
          toolId,
          result: '',
          error: output,
          timestamp,
          attribution,
          ...refreshed,
        }
      : {
          type: AgentEventType.ToolComplete,
          toolId,
          result: output,
          timestamp,
          attribution,
          ...refreshed,
        };
  }

  #extractToolOutput(entry: WorkflowTool): string {
    const toolResponse = entry.tool_info?.tool_response;
    return typeof toolResponse === 'string'
      ? toolResponse
      : (toolResponse?.content ?? entry.content);
  }

  // Known limitation / edge-case: greedy matching can misattribute results when the chat log contains
  // multiple request/tool pairs with identical tool names and args (e.g. two `bash { command: 'ls' }`
  // calls). Both requests will match the first tool entry, losing the second tool's output.
  // This only affects session replay (mapChatLogToSessionEvents), not live streaming.
  // See: https://gitlab.com/gitlab-org/editor-extensions/gitlab-lsp/-/merge_requests/2900#note_3108821806
  #findMatchingToolExecution(chatLog: ChatLog[], requestIndex: number): number {
    const request = chatLog[requestIndex];
    if (request.message_type !== 'request' || isPlanApprovalRequest(request)) return -1;

    for (let i = requestIndex + 1; i < chatLog.length; i++) {
      const entry = chatLog[i];
      if (
        entry.message_type === 'tool' &&
        entry.tool_info !== null &&
        entry.tool_info.name === request.tool_info.name &&
        this.#argsMatch(entry.tool_info.args, request.tool_info.args)
      ) {
        return i;
      }
    }
    return -1;
  }

  #hasMatchingRequest(chatLog: ChatLog[], toolIndex: number): boolean {
    const tool = chatLog[toolIndex];
    if (tool.message_type !== 'tool' || tool.tool_info === null) return false;

    for (let i = toolIndex - 1; i >= 0; i--) {
      const entry = chatLog[i];
      if (
        entry.message_type === 'request' &&
        !isPlanApprovalRequest(entry) &&
        entry.tool_info.name === tool.tool_info.name &&
        this.#argsMatch(entry.tool_info.args, tool.tool_info.args)
      ) {
        return true;
      }
    }
    return false;
  }
}
